import { getRegionFromUrl } from "@certn/utils";

import i18n from "./i18n";
import { UserDetails } from "./types";
import { DOMAIN, AUTH0_AUDIENCE } from "../constants";
import { components } from "@certn/api/generated-schemas/client-portal";

export type CobrandingUrlsResponse =
  components["schemas"]["CobrandingUrlsResponse"];

export function getDomain() {
  if (DOMAIN) {
    return DOMAIN;
  }

  if (AUTH0_AUDIENCE) {
    return AUTH0_AUDIENCE;
  }

  throw new Error(
    "No domain was specified. Have you set up your .env file? See the README for more information."
  );
}

export function createToken(input: string = "") {
  return window.btoa(input);
}

export function pendoMetadata(userDetails: UserDetails | undefined) {
  const client_account = userDetails?.client_account;

  const rolesList =
    userDetails?.roles
      ?.map((role) => role.name)
      .sort((a, b) => a.localeCompare(b)) || [];

  const reducedCustomerGroupLists = userDetails?.customer_groups?.reduce(
    (groupedCustomerGroup, currentCustomerGroup) => {
      if (currentCustomerGroup?.permissible_purpose) {
        groupedCustomerGroup.permissible_purpose.add(
          currentCustomerGroup.permissible_purpose
        );
      }
      if (currentCustomerGroup?.billing_profile?.currency) {
        groupedCustomerGroup.currency.add(
          currentCustomerGroup.billing_profile.currency
        );
      }
      return groupedCustomerGroup;
    },
    {
      permissible_purpose: new Set<string>(),
      currency: new Set<string>(),
    }
  );

  const customerGroupLists = {
    permissible_purpose: Array.from(
      reducedCustomerGroupLists?.permissible_purpose ?? []
    ).sort(),
    currency: Array.from(reducedCustomerGroupLists?.currency ?? []).sort(),
  };

  const visitorData = {
    id: userDetails?.auth0_id,
    email: userDetails?.auth0_email,
    full_name: userDetails?.auth0_full_name,
    primary_region: userDetails?.auth0_primary_region,
    locale: i18n.language,
    created: userDetails?.created,
    modified: userDetails?.modified,
    permission_level: rolesList.join(", "),
    permission_level_list: rolesList,
    region_from_url: getRegionFromUrl(),
  };

  const accountData = {
    id: client_account?.auth0_id,
    display_name: client_account?.auth0_display_name,
    name: client_account?.auth0_name,
    created: client_account?.created,
    modified: client_account?.modified,
    source: client_account?.source,
    country: client_account?.address?.country,
    administrative_division: client_account?.address?.administrative_division_1,
    permissible_purpose: customerGroupLists?.permissible_purpose.join(", "),
    currency: customerGroupLists?.currency.join(", "),
    permissible_purpose_list: customerGroupLists?.permissible_purpose,
    currency_list: customerGroupLists?.currency,
  };

  return {
    visitor: visitorData,
    account: accountData,
  };
}

/**
 * Appends the bucket URL to any URL properties from branding assets
 * @param brandingAssets - The branding assets object containing URLs and bucket
 * @returns An object with the same structure but with bucket-prefixed URLs
 */
export function appendBucketToUrls(
  brandingAssets: CobrandingUrlsResponse | undefined
): CobrandingUrlsResponse | undefined {
  if (!brandingAssets || !brandingAssets.bucket) {
    return brandingAssets;
  }

  const { bucket, ...urlProps } = brandingAssets;

  const processedUrls: CobrandingUrlsResponse = {
    bucket,
    primary_brand_color: brandingAssets.primary_brand_color,
    optimal_text_color: brandingAssets.optimal_text_color,
    whitelabel_report: brandingAssets.whitelabel_report,
    org_logo_url: null,
    org_icon_url: null,
    org_icon_url_dark_mode: null,
    org_logo_url_dark_mode: null,
  };

  // Process only URL properties (not boolean or color fields)
  const urlKeys = [
    "org_logo_url",
    "org_icon_url",
    "org_icon_url_dark_mode",
    "org_logo_url_dark_mode",
  ] as const;

  urlKeys.forEach((key) => {
    const value = urlProps[key];
    if (value && typeof value === "string") {
      // Ensure bucket URL ends with / before appending the value
      const bucketWithSlash = bucket.endsWith("/") ? bucket : `${bucket}/`;
      processedUrls[key] = `${bucketWithSlash}${value}`;
    } else {
      // Keep null values as they are
      processedUrls[key] = value;
    }
  });

  return processedUrls;
}
